import dayjs from 'dayjs';

export const getObjectId = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return value._id || value.id || value.value;
};

export const resolveHistoryScheduleId = (row: any): string | undefined =>
    row?.scheduleId ||
    row?.schedule_id ||
    row?.scheduleObjectId ||
    getObjectId(row?.schedule) ||
    getObjectId(row?.scheduleObject) ||
    row?._id ||
    row?.id;

export const resolveHistoryCoachId = (row: any): string | undefined =>
    row?.coachId || row?.trainerId || getObjectId(row?.coach) || getObjectId(row?.trainer);

export const resolveHistoryMemberId = (row: any): string | undefined =>
    row?.memberId || getObjectId(row?.member) || getObjectId(row?.memberInfo);

export const resolveExplicitGroupSessionId = (row: any): string | undefined =>
    row?.sessionId ||
    row?.groupScheduleId ||
    row?.groupSessionId ||
    getObjectId(row?.session) ||
    getObjectId(row?.groupSchedule) ||
    getObjectId(row?.groupSession);

export const resolveGroupSessionId = (row: any): string | undefined =>
    resolveExplicitGroupSessionId(row) || row?.scheduleId || getObjectId(row?.schedule);

export const flattenScheduleLikeItems = (value: any): any[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.flatMap((item) => flattenScheduleLikeItems(item));
    if (typeof value !== 'object') return [];
    if (value.startTime && value.endTime) return [value];
    return Object.values(value).flatMap((item) => flattenScheduleLikeItems(item));
};

const sameInstant = (a?: string, b?: string) => {
    if (!a || !b) return false;
    const left = dayjs(a);
    const right = dayjs(b);
    return left.isValid() && right.isValid() && left.valueOf() === right.valueOf();
};

export const findMatchingPtSchedule = (dailyScheduleData: any, row: any, fallbackMemberId?: string): any | undefined => {
    const rowMemberId = resolveHistoryMemberId(row) || fallbackMemberId;
    const rowCoachId = resolveHistoryCoachId(row);
    const rowMembershipId = row?.membershipId || getObjectId(row?.membership);
    const items = flattenScheduleLikeItems(dailyScheduleData).filter((item) => item?.type !== 'emptySchedule');

    return (
        items.find((item) => {
            const itemMemberId = resolveHistoryMemberId(item);
            if (rowMemberId && itemMemberId && itemMemberId !== rowMemberId) return false;
            if (!sameInstant(item.startTime, row?.startTime)) return false;
            if (row?.endTime && item.endTime && !sameInstant(item.endTime, row.endTime)) return false;
            if (rowCoachId && item.coachId && item.coachId !== rowCoachId) return false;
            if (rowMembershipId && item.membershipId && item.membershipId !== rowMembershipId) return false;
            return true;
        }) ||
        items.find((item) => {
            const itemMemberId = resolveHistoryMemberId(item);
            return (
                (!rowMemberId || !itemMemberId || itemMemberId === rowMemberId) &&
                sameInstant(item.startTime, row?.startTime)
            );
        })
    );
};

export const findMatchingGroupSession = (sessions: any[], row: any): any | undefined => {
    const rowCoachId = resolveHistoryCoachId(row);
    const rowTitle = row?.title || row?.sessionTitle;

    return (
        sessions.find((session) => {
            if (!sameInstant(session.startTime, row?.startTime)) return false;
            if (row?.endTime && session.endTime && !sameInstant(session.endTime, row.endTime)) return false;
            if (rowCoachId && session.coachId && session.coachId !== rowCoachId) return false;
            if (rowTitle && session.title && session.title !== rowTitle) return false;
            return true;
        }) ||
        sessions.find((session) => {
            if (!sameInstant(session.startTime, row?.startTime)) return false;
            if (row?.endTime && session.endTime && !sameInstant(session.endTime, row.endTime)) return false;
            if (rowCoachId && session.coachId && session.coachId !== rowCoachId) return false;
            return true;
        }) ||
        sessions.find((session) => sameInstant(session.startTime, row?.startTime))
    );
};
